import React, { useMemo } from "react";
import { Info, Quote as QuoteIcon, AlertTriangle, Lightbulb, List } from "lucide-react";

const CALLOUT_TYPES = {
  info: { icon: Info, cls: "bg-blue-50 border-blue-200 text-blue-900" },
  tip: { icon: Lightbulb, cls: "bg-green-50 border-green-200 text-green-900" },
  warning: { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200 text-amber-900" },
};

const inlineFormat = (s) => {
  const parts = [];
  let rest = s;
  const regex = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/;
  while (rest.length) {
    const m = rest.match(regex);
    if (!m) { parts.push(rest); break; }
    const idx = m.index;
    if (idx > 0) parts.push(rest.slice(0, idx));
    const tok = m[0];
    if (tok.startsWith("[")) {
      const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      parts.push(<a key={parts.length} href={lm[2]} className="text-green-700 font-semibold underline-offset-4 hover:underline">{lm[1]}</a>);
    } else if (tok.startsWith("**")) parts.push(<strong key={parts.length}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) parts.push(<code key={parts.length} className="rounded bg-slate-100 px-1 py-0.5 text-[12px] font-mono text-slate-800">{tok.slice(1, -1)}</code>);
    else parts.push(<em key={parts.length}>{tok.slice(1, -1)}</em>);
    rest = rest.slice(idx + tok.length);
  }
  return parts;
};

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const MarkdownView = ({ text, withTOC = false }) => {
  const blocks = useMemo(() => {
    if (!text) return { items: [], toc: [] };
    const lines = text.split("\n");
    const items = [];
    const toc = [];
    let buf = [];
    let i = 0;
    const flushList = () => {
      if (buf.length) {
        items.push({ type: "ul", lines: buf });
        buf = [];
      }
    };
    while (i < lines.length) {
      const line = lines[i];
      // Callout block: :::tip Title \n body \n :::
      const cm = line.match(/^:::(info|tip|warning)(?:\s+(.+))?$/);
      if (cm) {
        flushList();
        const ctype = cm[1];
        const ctitle = cm[2] || "";
        const cbody = [];
        i++;
        while (i < lines.length && !/^:::\s*$/.test(lines[i])) { cbody.push(lines[i]); i++; }
        items.push({ type: "callout", ctype, title: ctitle, body: cbody.join("\n") });
        i++; continue;
      }
      // Blockquote
      if (/^>\s?/.test(line)) {
        flushList();
        items.push({ type: "quote", text: line.replace(/^>\s?/, "") });
      } else if (/^#{1,6}\s/.test(line)) {
        flushList();
        const level = line.match(/^(#+)/)[1].length;
        const txt = line.replace(/^#+\s*/, "");
        const id = slugify(txt);
        items.push({ type: "h", level, text: txt, id });
        if (level === 2) toc.push({ id, text: txt });
      } else if (/^---+\s*$/.test(line)) {
        flushList();
        items.push({ type: "hr" });
      } else if (/^\s*[-*]\s+/.test(line)) {
        buf.push(line.replace(/^\s*[-*]\s+/, ""));
      } else if (/^\s*\d+\.\s+/.test(line)) {
        buf.push(line.replace(/^\s*\d+\.\s+/, ""));
      } else if (line.trim() === "") {
        flushList();
      } else {
        flushList();
        items.push({ type: "p", text: line });
      }
      i++;
    }
    flushList();
    return { items, toc };
  }, [text]);

  if (!text) return null;

  const renderBlock = (b, idx) => {
    if (b.type === "h") {
      const cls = {
        1: "mt-8 mb-4 font-display text-3xl font-bold text-slate-900 tracking-tight",
        2: "mt-12 mb-3 font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight scroll-mt-24",
        3: "mt-8 mb-2 font-display text-xl font-bold text-slate-900",
      }[b.level] || "mt-4 mb-2 font-semibold text-slate-900";
      return <h3 key={idx} id={b.id} className={cls}>{inlineFormat(b.text)}</h3>;
    }
    if (b.type === "p") return <p key={idx} className="my-4 text-base leading-relaxed text-slate-700">{inlineFormat(b.text)}</p>;
    if (b.type === "ul") return (
      <ul key={idx} className="my-4 space-y-2 list-disc pl-5 text-base text-slate-700">
        {b.lines.map((it, i) => <li key={i}>{inlineFormat(it)}</li>)}
      </ul>
    );
    if (b.type === "quote") return (
      <blockquote key={idx} className="my-6 border-l-4 border-green-500 bg-green-50 pl-5 pr-4 py-4 rounded-r-xl">
        <QuoteIcon className="h-5 w-5 text-green-600 mb-1" />
        <p className="italic text-slate-700 leading-relaxed">{inlineFormat(b.text)}</p>
      </blockquote>
    );
    if (b.type === "callout") {
      const cfg = CALLOUT_TYPES[b.ctype];
      const Icon = cfg.icon;
      return (
        <div key={idx} className={`my-6 rounded-2xl border-2 ${cfg.cls} p-5`}>
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {b.title && <p className="font-display font-bold text-base">{b.title}</p>}
          </div>
          <p className="mt-2 text-sm leading-relaxed">{inlineFormat(b.body)}</p>
        </div>
      );
    }
    if (b.type === "hr") return <hr key={idx} className="my-8 border-slate-200" />;
    return null;
  };

  return (
    <div className="prose-content">
      {withTOC && blocks.toc.length > 1 && (
        <nav className="my-8 rounded-2xl bg-slate-50 border border-slate-100 p-5" data-testid="article-toc">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <List className="h-3.5 w-3.5" /> En este artículo
          </p>
          <ol className="mt-3 space-y-1.5 list-decimal pl-5 text-sm text-slate-700">
            {blocks.toc.map((t, i) => (
              <li key={i}>
                <a href={`#${t.id}`} className="hover:text-green-700">{t.text}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      {blocks.items.map(renderBlock)}
    </div>
  );
};

export default MarkdownView;
